import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { View, Text, TouchableOpacity, Pressable, Platform } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import Constants from 'expo-constants';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '../firebaseConfig';
import { useNavigation } from '../context/NavigationContext';
import { useAuth, ADMIN_PREVIEW_PLANS } from '../context/AuthContext';
import appJson from '../app.json';
import {
  stripeUiTierFromHostname,
  effectiveStripeBillingMode,
  billingModeDisplay,
  selectPublishableKey,
} from '../utils/stripeBillingMode';

const APP_VERSION = appJson?.expo?.version || Constants?.expoConfig?.version || '1.0.0';

const PREVIEW_PLAN_COLORS = {
  FREE:     '#6B7280',
  BASIC:    '#3B82F6',
  PRO:      '#A855F7',
  ULTIMATE: '#F59E0B',
};

const getBase = () => (
  (typeof process !== 'undefined' && process?.env?.EXPO_PUBLIC_FUNCTIONS_BASE_URL)
  || (Constants?.expoConfig?.extra?.EXPO_PUBLIC_FUNCTIONS_BASE_URL)
  || (Constants?.expoConfig?.extra?.FUNCTIONS_BASE_URL)
  || (Constants?.manifest?.extra?.FUNCTIONS_BASE_URL)
);

function detectEnvironment() {
  if (Platform.OS !== 'web') return { name: 'Mobile', color: '#06B6D4' };
  try {
    const host = window?.location?.hostname || '';
    if (host.includes('localhost') || host.includes('127.0.0.1')) return { name: 'Development', color: '#3B82F6' };
    if (host.includes('staging')) return { name: 'Staging', color: '#F59E0B' };
    return { name: 'Production', color: '#10B98F' };
  } catch {
    return { name: 'Unknown', color: '#6B7280' };
  }
}

function getExpoExtra() {
  try {
    return Constants?.expoConfig?.extra || Constants?.manifest?.extra || {};
  } catch {
    return {};
  }
}

function envShortName(envName) {
  if (envName === 'Development') return 'Dev';
  if (envName === 'Production') return 'Prod';
  if (envName === 'Staging') return 'Stg';
  return envName;
}

function formatTimestamp(ms) {
  if (!ms) return 'Never';
  const d = new Date(ms);
  const diff = Date.now() - ms;
  const mins = Math.floor(diff / 60000);
  const hrs = Math.floor(diff / 3600000);
  const days = Math.floor(diff / 86400000);
  let ago;
  if (mins < 1) ago = 'just now';
  else if (mins < 60) ago = `${mins}m ago`;
  else if (hrs < 24) ago = `${hrs}h ago`;
  else ago = `${days}d ago`;
  return `${d.toLocaleDateString()} ${d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })} (${ago})`;
}

export default function AdminEnvPill() {
  const { isAdmin } = useNavigation();
  const { userProfile, adminPreviewPlan, setAdminPreviewPlan, clearAdminPreviewPlan } = useAuth();
  const [open, setOpen] = useState(false);
  const [hovered, setHovered] = useState(false);
  const rootRef = useRef(null);
  const [meta, setMeta] = useState(null);
  const [metaError, setMetaError] = useState(false);
  const [stripeSettings, setStripeSettings] = useState({
    useLiveInDevelopment: false,
    useLiveInStaging: false,
  });

  const showPill = userProfile?.preferences?.showEnvPill !== false;

  useEffect(() => {
    if (!isAdmin || !showPill || Platform.OS !== 'web') return undefined;
    const unsub = onSnapshot(
      doc(db, 'site_config', 'stripe_settings'),
      (snap) => {
        const d = snap.exists() ? snap.data() : {};
        setStripeSettings({
          useLiveInDevelopment: d.useLiveInDevelopment === true,
          useLiveInStaging: d.useLiveInStaging === true,
        });
      },
      () => {}
    );
    return () => unsub();
  }, [isAdmin, showPill]);

  const fetchMeta = useCallback(async () => {
    try {
      const base = getBase();
      if (!base) return;
      const res = await fetch(`${base}/getAppMetadata`);
      const json = await res.json();
      if (json.success && json.data) setMeta(json.data);
    } catch {
      setMetaError(true);
    }
  }, []);

  useEffect(() => {
    if (isAdmin && showPill) fetchMeta();
  }, [isAdmin, showPill, fetchMeta]);

  useEffect(() => {
    if (open && isAdmin && showPill) fetchMeta();
  }, [open, isAdmin, showPill, fetchMeta]);

  const isOpen = open;

  useEffect(() => {
    if (!open || Platform.OS !== 'web') return undefined;
    const handlePointerDown = (event) => {
      if (rootRef.current?.contains?.(event.target)) return;
      setOpen(false);
      setHovered(false);
    };
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') setOpen(false);
    };
    document.addEventListener('pointerdown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('pointerdown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [open]);

  const browserTier = useMemo(() => {
    try {
      const host = typeof window !== 'undefined' ? window?.location?.hostname || '' : '';
      return stripeUiTierFromHostname(host);
    } catch {
      return 'development';
    }
  }, []);

  const serverStripeModeLabel = useMemo(() => {
    const m = meta?.stripe_billing_mode;
    if (m === 'live' || m === 'test') return billingModeDisplay(m);
    return { label: '—', shortLabel: '—', color: '#6B7280' };
  }, [meta?.stripe_billing_mode]);

  if (!isAdmin || !showPill) return null;
  if (Platform.OS !== 'web') return null;

  const env = detectEnvironment();
  const browserStripeMode = effectiveStripeBillingMode(browserTier, stripeSettings);
  const stripeMode = billingModeDisplay(browserStripeMode);
  const extra = getExpoExtra();
  const pkForBrowser = selectPublishableKey(extra, browserStripeMode);
  const stripeModeForPill = !pkForBrowser
    ? { label: 'Missing key for mode', shortLabel: '?', color: '#6B7280' }
    : stripeMode;

  const functionsUrl = getBase();
  const latestVersion = meta?.version || APP_VERSION;
  const isDev = env.name === 'Development';
  const versionCurrent = isDev || APP_VERSION === latestVersion;
  const versionColor = versionCurrent ? '#10B98F' : '#EF4444';
  const envColor = env.name === 'Production'
    ? (versionCurrent ? '#10B98F' : '#EF4444')
    : env.color;
  const stripeMismatch = browserStripeMode !== meta?.stripe_billing_mode && !!meta?.stripe_billing_mode;

  return (
    <View ref={rootRef} style={styles.root}>
      {isOpen && (
      <View
        pointerEvents="auto"
        style={[styles.panel, styles.panelOpen]}
      >
        <View style={styles.panelHeader}>
          <View style={[styles.panelIcon, { borderColor: envColor + '55', backgroundColor: envColor + '18' }]}>
            <Ionicons name="shield-checkmark-outline" size={15} color={envColor} />
          </View>
          <View style={styles.panelHeading}>
            <Text style={styles.panelTitle}>Admin</Text>
            <Text style={styles.panelSubtitle}>{env.name} · v{APP_VERSION}</Text>
          </View>
          <TouchableOpacity onPress={() => setOpen(false)} style={styles.closeButton} accessibilityRole="button" accessibilityLabel="Close admin panel">
            <Ionicons name="close" size={15} color="rgba(255,255,255,0.55)" />
          </TouchableOpacity>
        </View>

        <View style={styles.healthRow}>
          <HealthBadge color={versionColor} dot>{versionCurrent ? 'Current' : 'Update'}</HealthBadge>
          <HealthBadge color={envColor}>{envShortName(env.name)}</HealthBadge>
          <HealthBadge color={stripeModeForPill.color}>{stripeModeForPill.shortLabel}</HealthBadge>
        </View>

        <SectionTitle icon="cloud-upload-outline">Deployments</SectionTitle>
        <View style={styles.sectionCard}>
          <Row label="Production" value={meta?.production_version ? `v${meta.production_version}` : '—'} />
          <Text style={styles.rowMeta}>{formatTimestamp(meta?.production_deploy_at)}</Text>
          {meta?.production_commit && <Row label="Commit" value={meta.production_commit.slice(0, 7)} mono small />}
          <Divider />
          <Row label="Staging" value={meta?.staging_version ? `v${meta.staging_version}` : '—'} />
          <Text style={styles.rowMeta}>{formatTimestamp(meta?.staging_deploy_at)}</Text>
          {meta?.staging_commit && <Row label="Commit" value={meta.staging_commit.slice(0, 7)} mono small />}
        </View>

        <SectionTitle icon="card-outline">Billing</SectionTitle>
        <View style={styles.sectionCard}>
          <Row label="Browser" value={stripeModeForPill.label} valueColor={stripeModeForPill.color} />
          <Row label="Functions" value={serverStripeModeLabel.label} valueColor={serverStripeModeLabel.color} />
          <Row label="Tier" value={meta?.deployment_tier || browserTier} small />
        </View>
        {stripeMismatch && (
          <View style={styles.warningCard}>
            <Ionicons name="warning-outline" size={12} color="#F59E0B" />
            <Text style={styles.warnText}>Browser and Functions Stripe modes differ.</Text>
          </View>
        )}

        <SectionTitle icon="eye-outline">Preview plan</SectionTitle>
        {adminPreviewPlan && (
          <TouchableOpacity onPress={clearAdminPreviewPlan} style={styles.resetBtn}>
            <Text style={styles.resetText}>Reset</Text>
          </TouchableOpacity>
        )}
        <View style={styles.previewChips}>
          {Object.keys(ADMIN_PREVIEW_PLANS).map(key => {
            const active = adminPreviewPlan === key;
            const color = PREVIEW_PLAN_COLORS[key];
            return (
              <TouchableOpacity
                key={key}
                style={[styles.previewChip, { borderColor: active ? color : 'rgba(255,255,255,0.1)', backgroundColor: active ? color + '22' : 'transparent' }]}
                onPress={() => active ? clearAdminPreviewPlan() : setAdminPreviewPlan(key)}
                activeOpacity={0.7}
              >
                <Text style={[styles.previewChipText, { color: active ? color : 'rgba(255,255,255,0.45)' }]}>{key}</Text>
              </TouchableOpacity>
            );
          })}
        </View>

        <View style={styles.panelFooter}>
          {metaError ? (
            <Text style={styles.errorText}>Deploy metadata unavailable</Text>
          ) : functionsUrl ? (
            <Text style={styles.footerText} numberOfLines={1}>
              {meta?.deployed_by ? `${meta.deployed_by} · ` : ''}{new URL(functionsUrl).hostname}
            </Text>
          ) : null}
        </View>
      </View>
      )}

      {hovered && !open && (
        <View pointerEvents="none" style={styles.tooltip}>
          <Text style={styles.tooltipText}>Click to open</Text>
        </View>
      )}

      <Pressable
        style={({ hovered: isHovered, pressed }) => [
          styles.chip,
          (isHovered || open) && styles.chipExpanded,
          isHovered && styles.chipHovered,
          (isHovered || open) && styles.chipActive,
          pressed && styles.chipPressed,
          { borderColor: (isHovered || open) ? envColor + '80' : 'rgba(255,255,255,0.1)' },
        ]}
        onPress={() => setOpen(value => !value)}
        onHoverIn={() => setHovered(true)}
        onHoverOut={() => setHovered(false)}
        accessibilityRole="button"
        accessibilityLabel={`${open ? 'Close' : 'Open'} admin panel`}
      >
        {/* Persistent admin icon badge — always visible, this is the "square" when collapsed */}
        <View style={styles.chipIcon}>
          <Ionicons name="shield-checkmark-outline" size={14} color={(hovered || open) ? envColor : 'rgba(255,255,255,0.6)'} />
        </View>

        {/* Corner status dot — visible only when collapsed (square state) */}
        <View
          style={[
            styles.cornerDot,
            { backgroundColor: versionColor, opacity: (hovered || open) ? 0 : 1 },
          ]}
          pointerEvents="none"
        />

        {/* Expandable content — version + chevron, animates in via maxWidth */}
        <View
          style={[
            styles.chipExtra,
            (hovered || open) ? styles.chipExtraOpen : styles.chipExtraClosed,
          ]}
          pointerEvents="none"
        >
          <View style={[styles.statusDot, { backgroundColor: versionColor }]} />
          <Text style={styles.chipVersion}>v{APP_VERSION}</Text>
          <Ionicons name={(hovered || open) ? 'chevron-down' : 'chevron-up'} size={10} color="rgba(255,255,255,0.4)" />
        </View>
      </Pressable>
    </View>
  );
}

function HealthBadge({ color, dot, children }) {
  return (
    <View style={[styles.healthBadge, { backgroundColor: color + '18' }]}>
      {dot && <View style={[styles.statusDot, { backgroundColor: color }]} />}
      <Text style={[styles.healthText, { color }]}>{children}</Text>
    </View>
  );
}

function Row({ label, value, valueColor, small, mono }) {
  return (
    <View style={styles.row}>
      <Text style={styles.rowLabel}>{label}</Text>
      <Text
        style={[
          styles.rowValue,
          valueColor && { color: valueColor },
          small && { fontSize: 9 },
          mono && { fontFamily: Platform.OS === 'web' ? 'monospace' : undefined },
        ]}
        numberOfLines={1}
      >
        {value}
      </Text>
    </View>
  );
}

function Divider() {
  return <View style={styles.divider} />;
}

function SectionTitle({ icon, children }) {
  return (
    <View style={styles.sectionTitle}>
      <Ionicons name={icon} size={11} color="rgba(255,255,255,0.38)" />
      <Text style={styles.sectionTitleText}>{children}</Text>
    </View>
  );
}

const styles = {
  root: { position: 'fixed', bottom: 16, left: 16, zIndex: 99999, alignItems: 'flex-start' },
  chip: {
    flexDirection: 'row', alignItems: 'center', height: 28, maxWidth: 28,
    borderRadius: 8, backgroundColor: 'rgba(20,20,24,0.96)',
    backdropFilter: 'blur(12px)', WebkitBackdropFilter: 'blur(12px)',
    borderWidth: 1, borderColor: 'rgba(255,255,255,0.1)',
    shadowColor: '#000', shadowOffset: { width: 0, height: 3 }, shadowOpacity: 0.3, shadowRadius: 8, elevation: 6,
    overflow: 'hidden',
    transition: 'max-width 240ms cubic-bezier(0.22,1,0.36,1), transform 160ms ease, background-color 160ms ease, border-color 160ms ease, box-shadow 160ms ease',
  },
  chipExpanded: {
    maxWidth: 130,
  },
  chipHovered: { transform: [{ translateY: -1 }], backgroundColor: 'rgba(32,36,46,0.98)', boxShadow: '0 6px 18px rgba(30,80,220,0.22)' },
  chipActive: { backgroundColor: 'rgba(34,34,42,0.98)' },
  chipPressed: { transform: [{ scale: 0.96 }] },
  chipIcon: {
    width: 28, height: 28, alignItems: 'center', justifyContent: 'center', flexShrink: 0,
  },
  cornerDot: {
    position: 'absolute', right: 3, bottom: 3, width: 6, height: 6, borderRadius: 3,
    borderWidth: 1.5, borderColor: 'rgba(20,20,24,0.96)',
    transition: 'opacity 120ms ease',
  },
  chipExtra: {
    flexDirection: 'row', alignItems: 'center', gap: 5, height: 28,
    paddingRight: 9, whiteSpace: 'nowrap', overflow: 'hidden', flexShrink: 0,
    transition: 'max-width 240ms cubic-bezier(0.22,1,0.36,1), opacity 160ms ease',
  },
  chipExtraOpen: { maxWidth: 100, opacity: 1 },
  chipExtraClosed: { maxWidth: 0, opacity: 0 },
  statusDot: { width: 6, height: 6, borderRadius: 3 },
  chipVersion: { color: 'rgba(255,255,255,0.82)', fontSize: 9, fontWeight: '700', letterSpacing: -0.1 },
  tooltip: {
    position: 'absolute', left: 0, bottom: 32, backgroundColor: 'rgba(20,20,24,0.98)',
    borderWidth: 1, borderColor: 'rgba(255,255,255,0.1)', borderRadius: 6,
    paddingHorizontal: 8, paddingVertical: 4, whiteSpace: 'nowrap', boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
  },
  tooltipText: { color: 'rgba(255,255,255,0.7)', fontSize: 10, fontWeight: '500' },
  panel: {
    position: 'absolute', left: 0, bottom: 36, width: 250, maxHeight: 'calc(100vh - 82px)', overflowY: 'auto',
    backgroundColor: 'rgba(20,20,24,0.97)', backdropFilter: 'blur(16px)', WebkitBackdropFilter: 'blur(16px)',
    borderRadius: 12, borderWidth: 1, borderColor: 'rgba(255,255,255,0.1)', padding: 12,
    shadowColor: '#000', shadowOffset: { width: 0, height: 8 }, shadowOpacity: 0.45, shadowRadius: 20, elevation: 10,
    transformOrigin: 'bottom left',
    transition: 'opacity 200ms cubic-bezier(0.22,1,0.36,1), transform 200ms cubic-bezier(0.22,1,0.36,1)',
  },
  panelOpen: { opacity: 1, transform: [{ translateY: 0 }] },
  panelClosed: { opacity: 0, transform: [{ translateY: 8 }] },
  panelHeader: { flexDirection: 'row', alignItems: 'center', marginBottom: 10 },
  panelIcon: { width: 28, height: 28, borderRadius: 7, borderWidth: 1, alignItems: 'center', justifyContent: 'center', marginRight: 8 },
  panelHeading: { flex: 1 },
  panelTitle: { color: 'rgba(255,255,255,0.92)', fontSize: 12, fontWeight: '700' },
  panelSubtitle: { color: 'rgba(255,255,255,0.4)', fontSize: 9, marginTop: 1 },
  closeButton: { width: 26, height: 26, borderRadius: 6, alignItems: 'center', justifyContent: 'center', backgroundColor: 'rgba(255,255,255,0.04)' },
  healthRow: { flexDirection: 'row', gap: 5, marginBottom: 11 },
  healthBadge: { flexDirection: 'row', alignItems: 'center', gap: 4, borderRadius: 20, paddingHorizontal: 7, paddingVertical: 3 },
  healthText: { fontSize: 8, fontWeight: '700', textTransform: 'uppercase', letterSpacing: 0.3 },
  sectionTitle: { flexDirection: 'row', alignItems: 'center', gap: 4, marginTop: 2, marginBottom: 5 },
  sectionTitleText: { color: 'rgba(255,255,255,0.42)', fontSize: 9, fontWeight: '700', textTransform: 'uppercase', letterSpacing: 0.6 },
  sectionCard: { backgroundColor: 'rgba(255,255,255,0.025)', borderWidth: 1, borderColor: 'rgba(255,255,255,0.06)', borderRadius: 8, paddingHorizontal: 9, paddingVertical: 6, marginBottom: 9 },
  row: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', paddingVertical: 2, gap: 10 },
  rowLabel: { color: 'rgba(255,255,255,0.45)', fontSize: 10, fontWeight: '500', flexShrink: 0 },
  rowValue: { color: 'rgba(255,255,255,0.85)', fontSize: 10, fontWeight: '600', textAlign: 'right' },
  rowMeta: { color: 'rgba(255,255,255,0.28)', fontSize: 8, lineHeight: 11 },
  divider: { height: 1, backgroundColor: 'rgba(255,255,255,0.06)', marginVertical: 4 },
  warningCard: { flexDirection: 'row', alignItems: 'center', gap: 5, backgroundColor: 'rgba(245,158,11,0.07)', borderWidth: 1, borderColor: 'rgba(245,158,11,0.16)', borderRadius: 7, paddingHorizontal: 8, paddingVertical: 6, marginTop: -2, marginBottom: 9 },
  warnText: { color: 'rgba(245,158,11,0.78)', fontSize: 8, lineHeight: 11, flex: 1 },
  resetBtn: { paddingHorizontal: 7, paddingVertical: 2, borderRadius: 5, backgroundColor: 'rgba(239,68,68,0.1)', marginLeft: 'auto' },
  resetText: { color: 'rgba(248,113,113,0.9)', fontSize: 8, fontWeight: '600' },
  previewChips: { flexDirection: 'row', gap: 4 },
  previewChip: { flex: 1, alignItems: 'center', paddingVertical: 5, borderRadius: 6, borderWidth: 1 },
  previewChipText: { fontSize: 8, fontWeight: '700', letterSpacing: 0.3 },
  panelFooter: { marginTop: 10 },
  footerText: { color: 'rgba(255,255,255,0.22)', fontSize: 8, textAlign: 'center' },
  errorText: { color: 'rgba(239,68,68,0.72)', fontSize: 8, textAlign: 'center' },
};
